import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuroraBackground } from "@/components/AuroraBackground";
import { BrandMark } from "@/components/BrandMark";
import { ShieldCheck, Cpu, Lock, ArrowLeft } from "lucide-react";

const Loader = () => (
  <div className="min-h-screen grid place-items-center bg-ink-base">
    <div className="w-12 h-12 grid place-items-center border border-aurora-purple/40 rounded-sm bg-aurora-purple/10 animate-pulse">
      <Cpu className="w-6 h-6 text-aurora-purple" />
    </div>
  </div>
);

const AdminShell = ({ children }) => (
  <div className="relative min-h-screen text-white grid place-items-center px-4">
    <AuroraBackground variant="muted" />
    <div className="relative z-10 w-full max-w-md">
      <div className="glass rounded-lg p-8 border border-aurora-purple/20">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 grid place-items-center rounded-sm bg-aurora-purple/15 border border-aurora-purple/30">
            <ShieldCheck className="w-6 h-6 text-aurora-purple" />
          </div>
          <BrandMark size="md" subtitle="Console Administration" />
        </div>
        {children}
      </div>
      <p className="text-center mt-6 font-mono text-[10px] text-zinc-600 tracking-widest uppercase">
        Accès restreint · Espace réservé à l'atelier
      </p>
    </div>
  </div>
);

export const AdminGate = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) return <Loader />;

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (!user) {
    return (
      <AdminShell>
        <div className="font-mono text-xs text-aurora-purple tracking-[0.3em] uppercase mb-2">// Administration</div>
        <h1 className="font-display text-3xl font-bold uppercase tracking-tight mb-2">Connexion Admin</h1>
        <p className="text-zinc-400 text-sm mb-8">Authentifiez-vous avec le compte administrateur autorisé.</p>
        <button
          onClick={handleGoogle}
          data-testid="admin-google-login-btn"
          className="w-full flex items-center justify-center gap-3 px-5 py-3.5 bg-white text-black font-display font-bold uppercase tracking-wider rounded-sm hover:bg-zinc-200 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
          Connexion Google sécurisée
        </button>
        <button
          onClick={() => navigate("/")}
          data-testid="admin-back-home-btn"
          className="mt-4 w-full flex items-center justify-center gap-2 text-zinc-500 hover:text-white transition-colors font-mono text-xs uppercase tracking-widest"
        >
          <ArrowLeft className="w-4 h-4" /> Retour au site
        </button>
      </AdminShell>
    );
  }

  if (user.role !== "admin") {
    return (
      <AdminShell>
        <div className="w-12 h-12 grid place-items-center rounded-sm bg-signal-error/10 border border-signal-error/30 mb-5">
          <Lock className="w-6 h-6 text-signal-error" />
        </div>
        <h1 className="font-display text-2xl font-bold uppercase tracking-tight mb-2">Accès refusé</h1>
        <p className="text-zinc-400 text-sm mb-6">
          Le compte <span className="text-white font-mono">{user.email}</span> n'a pas les droits administrateur.
        </p>
        <button
          onClick={() => navigate("/client")}
          data-testid="admin-goto-client-btn"
          className="w-full px-5 py-3 bg-aurora-cyan/10 border border-aurora-cyan/30 text-aurora-cyan font-display font-bold uppercase tracking-wider rounded-sm hover:bg-aurora-cyan/20 transition-colors"
        >
          Aller à mon espace client
        </button>
      </AdminShell>
    );
  }

  return children;
};
